import React, { useRef } from 'react';
import './model.cmp.scss';
import OutsideClickHandler from 'react-outside-click-handler';
import PLButton from '../common/button/PLButton';
import { useTranslation } from 'react-i18next';
import { useDialogAccessibility } from '../../hooks/use-dialog-accessibility';

let dialogSequence = 0;

interface ModelCmpProps {
    name: React.ReactNode;
    bodyComponent: React.ReactNode;
    onCancel: () => void;
    onSend: () => void;
    sendButtonText?: React.ReactNode;
    cancelButtonText?: React.ReactNode;
    closeOnOutsideClick?: boolean;
}

const ModelCmp = ({
    name,
    bodyComponent,
    onCancel,
    onSend,
    sendButtonText,
    cancelButtonText,
    closeOnOutsideClick = true,
}: ModelCmpProps) => {
    const { t } = useTranslation();
    const dialogRef = useRef<HTMLDivElement>(null);
    const idRef = useRef(`lms-dialog-${++dialogSequence}`);
    useDialogAccessibility(dialogRef, onCancel);
    const resolvedSendButtonText = sendButtonText || t("common.send");
    const resolvedCancelButtonText = cancelButtonText || t("common.cancel");
    return (
        <>
            <div className="overlay" aria-hidden="true"></div>
            <OutsideClickHandler
                onOutsideClick={() => {
                    if (closeOnOutsideClick) {
                        onCancel()
                    }
                }}
            >
                <div
                    className="model"
                    ref={dialogRef}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={`${idRef.current}-title`}
                    aria-describedby={`${idRef.current}-body`}
                    tabIndex={-1}
                >
                    <div className="modal-content">
                        {/* Header */}
                        <div className="modal-header">
                            <h2 className="modal-title" id={`${idRef.current}-title`}>{name}</h2>
                            <button
                                type="button"
                                onClick={onCancel}
                                className="close-button"
                                aria-label={t("accessibility.closeDialog")}
                            >
                                ×
                            </button>
                        </div>

                        {/* Body */}
                        <div className="modal-body" id={`${idRef.current}-body`}>
                            {bodyComponent}
                        </div>

                        {/* Buttons */}
                        <div className="modal-buttons">
                            <PLButton 
                                variant="secondary" 
                                size="lg"
                                onClick={onCancel}
                                className="button-flex"
                            >
                                {resolvedCancelButtonText}
                            </PLButton>
                            <PLButton 
                                variant="blue" 
                                size="lg"
                                onClick={onSend}
                                className="button-flex"
                            >
                                {resolvedSendButtonText}
                            </PLButton>
                        </div>
                    </div>
                </div>
            </OutsideClickHandler>
        </>
    );
};

export default ModelCmp;
